import {
  ICustomer,
  CustomerTypes,
  SEARCH_FIELDS,
  BookingType,
  IBookedByCustomerDetail,
  IBookedByValidation, servicePaymentType,
} from 'shared-types/index';
import PhoneNumberService from 'external-services/phone-number-service/index';
import moment from 'moment';
import {
  PHONE_VALIDATION_MESSAGE,
  PAYMENT_VALIDATION_MESSAGE,
  RECURRING_BOOKING_VALIDATION_MESSAGE,
  BOOKING_VALIDATION_MESSAGE,
  SBL_EMAIL_VALIDATION_MESSAGE,
  SBL_SMSVALIDATION_MESSAGE,
  EMAIL_INVALID_MESSAGE,
  EMAIL_REQUIRED_MESSAGE,
  EMAIL_TOO_LONG_MESSAGE,
  PHONE_ERROR_MESSAGE,
} from './types';
import BookingValidationService from 'external-services/booking-validation-service';
import { phoneNumberTypes } from 'external-services/phone-number-service/phoneNumber.types';

const NS = 'CustomerValidationService'; // jshint ignore:line

class CustomerValidationService {
  // private PHONE_VALIDATION_MESSAGE_ON_NON_PHONE_FIELD: string = 'You are searching on phone number, please enter 6 or more digits';

  public emailValidation(
    customer: ICustomer,
    isNoPayment?: boolean,
    isRecurringBooking?: boolean,
    isEditCustomer?: boolean,
    sblActive?: boolean,
    sblEmail?: boolean,
    isBookedBy?: boolean,
    isInlineCustomer = false
  ): {
    valid: boolean;
    msg?: string;
  } {

    // if we are already showing message for the payment for inline customer - no need to show another payment message - UI-1418
    const inlinePaymentMsg = isInlineCustomer && !isNoPayment;

    if (customer.email) {
      const emailValid = this.emailValidationCheck(customer.email);
      return { valid: emailValid.isEmailValid, msg: emailValid.emailErrorMsg };
    }
    // if enable bookedby, then customer email no requirement
    if (isBookedBy && !customer.email) {
      return {
        valid: true,
        msg: '',
      };
    }
    if (sblActive && sblEmail && !customer.email) {
      return {
        valid: false,
        msg: SBL_EMAIL_VALIDATION_MESSAGE,
      };
    }
    if (isEditCustomer && !customer.email && !customer.phone) {
      return {
        valid: false,
        msg: BOOKING_VALIDATION_MESSAGE,
      };
    } else if (!sblActive && !isNoPayment && !customer.phone && !customer.email && !inlinePaymentMsg) {
      return {
        valid: false,
        msg: PAYMENT_VALIDATION_MESSAGE,
      };
    } else if (!sblActive && isRecurringBooking && !customer.phone && !customer.email) {
      return {
        valid: false,
        msg: RECURRING_BOOKING_VALIDATION_MESSAGE,
      };
    }
    return {
      valid: true,
    };
  }

  public getCustomerValue(customer: ICustomer) {
    const searchFields =
      customer?.customerType === 'inline'
        ? [SEARCH_FIELDS.EMAIL, SEARCH_FIELDS.PHONE, SEARCH_FIELDS.FIRST_NAME, SEARCH_FIELDS.COMPANY]
        : Object.values(SEARCH_FIELDS);
    return Object.keys(customer)
      .filter(
        (key) =>
          searchFields.includes(key as SEARCH_FIELDS) &&
          customer[key as keyof ICustomer] &&
          (key !== SEARCH_FIELDS.FIRST_NAME || customer[key]?.toLowerCase() !== 'walk in')
      )
      .map((key) => {
        return {
          fieldName: key,
          value: customer[key as keyof ICustomer],
        };
      });
  }

  public emailValidationLink(customerEmail: string): {
    isEmailValid: boolean;
    emailErrorMsg?: string;
  } {
    return this.emailValidationCheck(customerEmail);
  }

  public emailValidationCheck(
    email: string,
    isEmailRequired = true
  ): {
    isEmailValid: boolean;
    emailErrorMsg?: string;
  } {
    const valid = BookingValidationService.validateEmail(email);

    if (!valid && !!email) {
      return {
        isEmailValid: false,
        emailErrorMsg: EMAIL_INVALID_MESSAGE,
      };
    } else if (isEmailRequired && email === '') {
      return {
        isEmailValid: false,
        emailErrorMsg: EMAIL_REQUIRED_MESSAGE,
      };
    }
    if (email) {
      const emailParts = email.split('@');
      if (
        emailParts[0].indexOf('.') === 0 ||
        emailParts[0].indexOf('.') === emailParts[0].length - 1 ||
        emailParts[1].indexOf('-') === 0 ||
        emailParts[1].indexOf('-') === emailParts[1].length - 1
      ) {
        return {
          isEmailValid: false,
          emailErrorMsg: EMAIL_INVALID_MESSAGE,
        };
      }
      if (email.length > 100 || emailParts[1].length > 63) {
        return {
          isEmailValid: false,
          emailErrorMsg: EMAIL_TOO_LONG_MESSAGE,
        };
      }
    }
    return {
      isEmailValid: true,
    };
  }

  public validateCustomer(
    customer: ICustomer,
    originCustomer: ICustomer,
    emailRequired: boolean,
    ignorePhoneValidation: boolean
  ): {
    isEdited: boolean;
    isValid: boolean;
    isBirthdayValid: boolean;
  } {
    let isValid = false;
    let isEdited = true;
    let isBirthdayValid = null;
    if (originCustomer) isEdited = this.hasCustomerChanged(originCustomer, customer);

    const hasName = customer && (customer.lastName || customer.firstName);
    const isInline = customer && customer.customerType === CustomerTypes.inline; // used for standbyList and waitList

    if (hasName) {
      const birthdayValid = this.isValidBirthday(customer);
      if (isInline) {
        // no phone & no email needed
        if (!emailRequired && ignorePhoneValidation) {
          isValid = true;
        }

        // just email needed
        if (emailRequired && ignorePhoneValidation && customer.email) {
          isValid = true;
        }

        // just phone needed for sbl
        if (!ignorePhoneValidation && customer.phone) {
          isValid = true;
        }

        // both phone and email needed
        if (emailRequired && !ignorePhoneValidation && customer.email && customer.phone) {
          isValid = true;
        }
        return {
          isValid,
          isEdited,
          isBirthdayValid,
        };
        // full customer details version
      } else if (birthdayValid) {
        isValid = true;
      }
      isBirthdayValid = birthdayValid;
    }
    return {
      isValid,
      isEdited,
      isBirthdayValid,
    };
  }

  public validatePhoneEmailForExistingCustomer = (
    customer: ICustomer,
    originCustomer: ICustomer
  ) => {
    if (originCustomer._id && customer._id && (!!originCustomer.phone || !!originCustomer.email)) {
      return !!customer.phone || !!customer.email;
    }
    return true;
  };

  public getPhoneValidationError(
    phoneNumberTooShort: boolean,
    phone: string,
    email: string,
    isPhoneTouched: boolean,
    isPhoneValid: boolean,
    displayErrors: any,
    isNoPayment?: boolean,
    isRecurringBooking?: boolean,
    isEditCustomer?: boolean,
    isPhoneDirty?: boolean,
    sblActive?: boolean,
    sblSms?: boolean,
    isBookedBy?: boolean,
    isInlineCustomer = false
  ) {
    if (phoneNumberTooShort && phone && isPhoneTouched) return PHONE_VALIDATION_MESSAGE;
    if (!phoneNumberTooShort && phone && !isPhoneValid) return PHONE_ERROR_MESSAGE;
    if (!phoneNumberTooShort && (isPhoneDirty || displayErrors) && sblActive && sblSms && !phone)
      return SBL_SMSVALIDATION_MESSAGE;
    if (!phoneNumberTooShort && isEditCustomer && !email && !phone)
      return BOOKING_VALIDATION_MESSAGE;
    if (!isEditCustomer && !sblActive) {
      // if we are already showing message for the payment for inline customer - no need to show another payment message - UI-1418
      const inlinePaymentMsg = isInlineCustomer && !isNoPayment;

      if (
        !phoneNumberTooShort &&
        (isPhoneDirty || displayErrors) &&
        !isNoPayment &&
        !email &&
        !phone &&
        !isBookedBy &&
        !inlinePaymentMsg
      )
        return PAYMENT_VALIDATION_MESSAGE;
      if (
        !phoneNumberTooShort &&
        (isPhoneDirty || displayErrors) &&
        isRecurringBooking &&
        !email &&
        !phone
      )
        return RECURRING_BOOKING_VALIDATION_MESSAGE;
    }

    return '';
  }

  public checkIsPhoneNumberType(phone: string): boolean {
    if (phone) {
      const parseNumber = PhoneNumberService.parseNumber(phone, true);
      return parseNumber && typeof parseNumber !== 'string';
    } else {
      return true;
    }
  }
  /**
   * for UI-604, error message is array of string, for error panel which will shows in booking creator top.
   * Need change to object if we need show error in specif field
   * this method will return all error immediately, need update if change to show error in specific field when click save.
   * @param bookedByDetail
   */

  public validateBookedBy(bookedByDetail: IBookedByCustomerDetail): IBookedByValidation {
    if (!bookedByDetail) {
      bookedByDetail = {};
    }
    const errors = [];
    let isBookedByEmailValid = true; // treat as true if no email input
    let isBookedByPhoneValid = true; // treat as true if no phone input
    const { email, firstName, lastName, phone } = bookedByDetail;
    const { nationalNumber, country } = PhoneNumberService.parseNumber(phone || '') || {
      nationalNumber: '',
      country: null,
    };
    if (!firstName && !lastName) {
      errors.push('A first or surname is required for Booked By');
    }
    if (!email && !nationalNumber) {
      errors.push('A phone or email is required for Booked By');
    }
    if (email) {
      const { isEmailValid, emailErrorMsg } = this.emailValidationCheck(email, false);
      if (!isEmailValid) {
        errors.push(emailErrorMsg);
      }
      isBookedByEmailValid = isEmailValid;
    }

    if (nationalNumber) {
      const isValidPhone = PhoneNumberService.formatNumber(
        nationalNumber,
        country,
        true,
        phoneNumberTypes.INTERNATIONAL
      );
      if (!isValidPhone) {
        errors.push(PHONE_ERROR_MESSAGE);
      }
      isBookedByPhoneValid = !!isValidPhone;
    }
    return {
      isBookedByValid: !errors.length,
      bookedByErrors: errors,
      isBookedByEmailValid,
      isBookedByPhoneValid,
    };
  }

  public checkRulesByBookingTypeAndCustomerType(
    bookingType: BookingType,
    sblEmailRequired: boolean,
    sblSmsRequired: boolean,
    customerType: CustomerTypes
  ): {
    emailRequired: boolean;
    ignorePhoneValidation: boolean;
    hideStopNotification: boolean;
    disableEmailSmsNotification: boolean;
  } {
    switch (bookingType) {
      case BookingType.StandbyList:
        return {
          emailRequired: sblEmailRequired,
          ignorePhoneValidation: !sblSmsRequired,
          hideStopNotification: true,
          disableEmailSmsNotification: false,
        };

      case BookingType.WaitList:
        return {
          emailRequired: false,
          ignorePhoneValidation: true,
          hideStopNotification: true,
          disableEmailSmsNotification: false,
        };

      default:
        if (customerType === CustomerTypes.inline) {
          return {
            emailRequired: false,
            ignorePhoneValidation: true,
            hideStopNotification: true,
            disableEmailSmsNotification: false,
          };
        } else {
          // vm.emailRequired = $scope.emailRequired || false;
          return {
            emailRequired: false,
            ignorePhoneValidation: false,
            hideStopNotification: false,
            disableEmailSmsNotification: false,
          };
        }
    }
  }

  public checkBookedByValid(
    bookedByErrors: string,
    isBookedBy: boolean,
    bookedByCustomerDetails: ICustomer
  ) {
    let isBookedByValid = true;
    if (bookedByErrors && isBookedBy) {
      isBookedByValid =
        Object.values(bookedByErrors).filter((i) => !!i).length === 0 &&
        (!!bookedByCustomerDetails.firstName || !!bookedByCustomerDetails.lastName) &&
        (!!bookedByCustomerDetails.phone || !!bookedByCustomerDetails.email);
    }
    return isBookedByValid;
  }

  private hasCustomerChanged(originCustomer: ICustomer, currentCustomer: ICustomer): boolean {
    const originPhone = PhoneNumberService.stripPhoneFormatting(originCustomer.phone);
    const currentPhone = PhoneNumberService.stripPhoneFormatting(currentCustomer.phone);
    if (originCustomer.firstName != currentCustomer.firstName) return true;
    else if (originCustomer.lastName != currentCustomer.lastName) return true;
    else if (originPhone != currentPhone) return true;
    else if (originCustomer.email != currentCustomer.email) return true;
    else return false;
  }

  private isValidBirthday(customer: ICustomer): boolean {
    let birthdayValid = false;
    // if day, month and year present validate as normal
    if (customer.birthdayYear && customer.birthdayMonth && customer.birthdayDate) {
      birthdayValid = moment(
        customer.birthdayYear + '-' + customer.birthdayMonth + '-' + customer.birthdayDate,
        'YYYY-MM-DD'
      ).isValid();
    }
    // if only day and month present, validate assuming it's a leap year
    else if (customer.birthdayMonth && customer.birthdayDate) {
      birthdayValid = moment(
        '1980-' + customer.birthdayMonth + '-' + customer.birthdayDate,
        'YYYY-MM-DD'
      ).isValid();
    }
    // in other cases don't validate
    else {
      birthdayValid = true;
    }

    return birthdayValid;
  }
}

const instance = new CustomerValidationService();
export default instance as CustomerValidationService;
